import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Polyline, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";
import { MapPin, Planet } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u?.startsWith("http") ? u : `${BACKEND_URL}${u}`);

function FitRoute({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) map.setView(points[0], 12);
    else if (points.length > 1) { try { map.fitBounds(points, { padding: [40, 40], maxZoom: 13 }); } catch (e) { console.debug("fitBounds skipped", e); } }
  }, [points, map]);
  return null;
}

export default function PublicStory() {
  const { id } = useParams();
  const [s, setS] = useState(undefined);

  useEffect(() => { api.get(`/public/stories/${id}`).then((r) => setS(r.data)).catch(() => setS(null)); }, [id]);

  if (s === undefined) return <div className="grid min-h-screen place-items-center text-zinc-400">Loading…</div>;
  if (s === null) return (
    <div className="grid min-h-screen place-items-center text-center">
      <div><Planet size={40} weight="fill" className="mx-auto text-zinc-300" /><p className="mt-3 font-display font-bold">Story not available</p><p className="text-sm text-muted-foreground">This story is not published.</p></div>
    </div>
  );

  const waypoints = (s.sections || []).filter((x) => x.lat != null && x.lon != null);
  const routePts = waypoints.map((w) => [w.lat, w.lon]);

  return (
    <div className="min-h-screen bg-white font-[IBM_Plex_Sans]">
      <article className="mx-auto max-w-3xl px-5 py-8">
        <div className="relative overflow-hidden rounded-lg">
          <img src={abs(s.cover_image_url)} alt="" className="h-80 w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
          <div className="absolute bottom-0 p-6 text-white">
            <h1 className="font-display text-4xl font-black leading-tight tracking-tight drop-shadow">{s.title}</h1>
            <p className="mt-1 flex items-center gap-1 text-sm text-white/85"><MapPin size={14} weight="fill" />{[s.city, s.country].filter(Boolean).join(", ")}</p>
          </div>
        </div>

        <p className="mt-6 text-lg leading-relaxed text-zinc-700">{s.intro}</p>

        {routePts.length > 0 && (
          <div className="mt-6 h-72 overflow-hidden rounded-lg border border-zinc-200">
            <MapContainer center={routePts[0]} zoom={12} scrollWheelZoom={false} className="h-full w-full">
              <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <FitRoute points={routePts} />
              {routePts.length > 1 && <Polyline positions={routePts} pathOptions={{ color: "#F27A54", weight: 3, dashArray: "6 6" }} />}
              {waypoints.map((w, i) => (
                <CircleMarker key={i} center={[w.lat, w.lon]} radius={9} pathOptions={{ color: "#fff", weight: 2, fillColor: "#F27A54", fillOpacity: 1 }}>
                  <Tooltip permanent direction="top" offset={[0, -8]}>{i + 1}. {w.heading}</Tooltip>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
        )}

        <div className="mt-8 space-y-10">
          {s.sections?.map((sec, i) => (
            <section key={i}>
              <h2 className="font-display text-2xl font-bold tracking-tight">{sec.heading}</h2>
              {sec.blurb && <p className="mt-1 leading-relaxed text-zinc-600">{sec.blurb}</p>}
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {sec.photos?.map((ph) => (
                  <img key={ph.id} src={abs(ph.image_url)} alt="" className="aspect-square w-full rounded-md object-cover" loading="lazy" />
                ))}
              </div>
            </section>
          ))}
        </div>

        <footer className="mt-12 flex items-center gap-2 border-t border-zinc-200 pt-6 text-xs text-zinc-400">
          <Planet size={14} weight="fill" className="text-[#F27A54]" /> Powered by PhotoPulse.pro™ — Pulse Photo Intelligence Engine
        </footer>
      </article>
    </div>
  );
}
